import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronRight, Zap, Clock, DollarSign, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { DEMO_PROJECT_ID, DEMO_TWIN_PANEL_READINESS } from '@/data/demo/preference-center/data'

type Step = 1 | 2 | 3
type QueryType = 'test_message' | 'test_concept' | 'test_pricing' | 'open_ended' | 'other'

interface BulkQuerySpec {
  question_text: string
  stimuli: { id: string; text: string; label: string }[]
  segments_filter: string[]
  confidence_floor: number
  include_low_confidence: boolean
}

const QUERY_TYPE_OPTIONS: { value: QueryType; label: string; desc: string }[] = [
  { value: 'test_message', label: 'Test a new message', desc: 'Compare how the panel would respond to 2-3 message variants' },
  { value: 'test_concept', label: 'Test a concept', desc: 'Predict how the panel receives a new product or feature concept' },
  { value: 'test_pricing', label: 'Test pricing copy', desc: 'How does the panel respond to pricing framing and anchors?' },
  { value: 'open_ended', label: 'Open-ended question', desc: 'Ask any question and see the predicted distribution across the panel' },
]

const SUGGESTION_CHIPS = [
  'Run on all validated twins',
  'Compare 2 messages',
  'Focus on one segment',
  'Skip guided setup',
]

const CONFIDENCE_FLOOR_OPTIONS = [
  { value: 0.3, label: '0.3 — include most twins' },
  { value: 0.4, label: '0.4 — default (validated only)' },
  { value: 0.5, label: '0.5 — high-confidence only' },
  { value: 0.6, label: '0.6 — most accurate only' },
]

export function BulkQueryComposerPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [step, setStep] = useState<Step>(1)
  const [queryType, setQueryType] = useState<QueryType | null>(null)
  const [spec, setSpec] = useState<BulkQuerySpec>({
    question_text: '',
    stimuli: [],
    segments_filter: [],
    confidence_floor: 0.4,
    include_low_confidence: false,
  })
  const [justSaved, setJustSaved] = useState(false)
  const [running, setRunning] = useState(false)
  const [readiness, setReadiness] = useState<{ twins_validated: number; twins_low_confidence: number } | null>(
    isDemo ? DEMO_TWIN_PANEL_READINESS : null
  )

  useEffect(() => {
    if (isDemo) return
    const token = localStorage.getItem('echo_session') ?? ''
    fetch(`/api/v1/projects/${projectId}/twin-panel/readiness`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() as Promise<{ twins_validated?: number; twins_low_confidence?: number }> : null)
      .then(d => {
        if (d && typeof d.twins_validated === 'number') {
          setReadiness({ twins_validated: d.twins_validated, twins_low_confidence: d.twins_low_confidence ?? 0 })
        } else if (d) {
          // not_built or missing twins — treat as zero so the banner shows
          setReadiness({ twins_validated: 0, twins_low_confidence: 0 })
        }
      })
      .catch(() => {})
  }, [projectId, isDemo])

  const twinsIncluded = readiness
    ? (spec.include_low_confidence
        ? readiness.twins_validated + readiness.twins_low_confidence
        : readiness.twins_validated)
    : 0
  const estimatedSecs = twinsIncluded > 0 ? Math.max(15, Math.round(twinsIncluded / 30)) : null
  const estimatedCost = twinsIncluded > 0 ? (twinsIncluded * 0.00038).toFixed(2) : null

  // Auto-save spec
  useEffect(() => {
    const t = setTimeout(() => { setJustSaved(true); setTimeout(() => setJustSaved(false), 2000) }, 800)
    return () => clearTimeout(t)
  }, [spec])

  async function handleRun() {
    if (!spec.question_text.trim()) return
    setRunning(true)
    try {
      const token = localStorage.getItem('echo_session') ?? ''
      const body = { ...spec }
      const res = await fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json() as { query_id: string }
      if (data.query_id) {
        navigate(`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/${data.query_id}`)
      } else if (isDemo) {
        navigate(`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/bq_pref_center_demo`)
      }
    } catch {
      if (isDemo) {
        navigate(`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/bq_pref_center_demo`)
      }
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-64px)]">
      {/* Mode tabs */}
      <div className="flex border-b border-border-gray px-6 pt-4 gap-6">
        <button className="pb-3 text-sm font-medium text-echo-indigo border-b-2 border-echo-indigo">
          Predict against twins
        </button>
        <button
          onClick={() => navigate(`/app/projects/${projectId}/respondents/query`)}
          className="pb-3 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors border-b-2 border-transparent hover:border-slate-300"
        >
          Ask the respondents
        </button>
      </div>

    {/* No-twins banner — only shown for real projects with 0 validated twins */}
    {!isDemo && readiness !== null && readiness.twins_validated === 0 && (
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between gap-4">
        <p className="text-sm text-amber-800">
          No validated twins yet. Upload a CSV and run the pipeline to build your Twin Panel before running bulk queries.
        </p>
        <button
          onClick={() => navigate(`/app/projects/${projectId}/upload`)}
          className="text-xs font-medium text-amber-900 underline underline-offset-2 whitespace-nowrap"
        >
          Upload data →
        </button>
      </div>
    )}

    <div className="flex flex-col lg:flex-row flex-1 border-t border-border-gray">
      {/* Left rail — chat / guided setup */}
      <div className="flex-1 flex flex-col p-6 lg:max-w-[45%] border-r border-border-gray" data-testid="chat-rail">
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6" data-testid="step-indicator">
          {([1, 2, 3] as const).map(s => (
            <div key={s} className="flex items-center gap-1.5">
              <div className={cn(
                'w-6 h-6 rounded-full text-xs flex items-center justify-center font-medium',
                step === s ? 'bg-echo-indigo text-white' : step > s ? 'bg-validation-green text-white' : 'bg-soft-panel text-slate-400'
              )}>
                {step > s ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : s}
              </div>
              <span className={cn('text-xs', step === s ? 'text-deep-ink font-medium' : 'text-slate-400')}>
                {s === 1 ? 'Question type' : s === 2 ? 'Research question' : 'Review & run'}
              </span>
              {s < 3 && <ChevronRight className="h-3 w-3 text-slate-300" strokeWidth={1.75} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4 flex-1">
            <p className="text-sm text-slate-700 font-medium">What do you want to learn from the twin panel?</p>
            <div className="space-y-2">
              {QUERY_TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  data-testid="scaffolded-option"
                  onClick={() => { setQueryType(opt.value); setStep(2) }}
                  className={cn(
                    'w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors',
                    queryType === opt.value
                      ? 'border-echo-indigo bg-echo-indigo/5 text-deep-ink'
                      : 'border-border-gray hover:border-echo-indigo/40 hover:bg-soft-panel/50 text-slate-700'
                  )}
                >
                  <p className="font-medium">{opt.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
            {/* Suggestion chips */}
            <div className="flex flex-wrap gap-2 pt-2">
              {SUGGESTION_CHIPS.map(chip => (
                <button
                  key={chip}
                  onClick={() => chip === 'Skip guided setup' ? setStep(2) : undefined}
                  className="text-xs px-3 py-1.5 rounded-full border border-border-gray text-slate-500 hover:border-echo-indigo/40 hover:text-deep-ink transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 flex-1">
            <p className="text-sm text-slate-700 font-medium">
              {queryType === 'test_message' ? 'What messages do you want to compare?' : 'What question do you want to ask the panel?'}
            </p>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1.5">Research question</label>
              <textarea
                data-testid="research-question-input"
                className="w-full border border-border-gray rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-echo-indigo resize-none"
                rows={3}
                placeholder="e.g. Which preference-center framing reduces opt-out intent most?"
                value={spec.question_text}
                onChange={e => setSpec(s => ({ ...s, question_text: e.target.value }))}
              />
            </div>
            {(queryType === 'test_message' || queryType === 'test_pricing' || queryType === 'test_concept') && (
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1.5">Stimuli to compare</label>
                {spec.stimuli.length === 0 && (
                  <button
                    className="text-sm text-echo-indigo hover:underline"
                    onClick={() => setSpec(s => ({
                      ...s,
                      stimuli: [
                        { id: 'stim_a', label: 'Option A', text: '' },
                        { id: 'stim_b', label: 'Option B', text: '' },
                      ],
                    }))}
                  >
                    + Add stimuli
                  </button>
                )}
                {spec.stimuli.map((stim, i) => (
                  <input
                    key={stim.id}
                    className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm mt-2 focus:outline-none focus:ring-1 focus:ring-echo-indigo"
                    placeholder={`${stim.label}: enter the text...`}
                    value={stim.text}
                    onChange={e => {
                      const updated = [...spec.stimuli]
                      updated[i] = { ...updated[i], text: e.target.value }
                      setSpec(s => ({ ...s, stimuli: updated }))
                    }}
                  />
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>Back</Button>
              <Button variant="primary" size="sm" onClick={() => setStep(3)} disabled={!spec.question_text.trim()}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 flex-1">
            <p className="text-sm text-slate-700 font-medium">Review and run</p>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-400 mb-1">Research question</p>
                <p className="text-sm text-slate-700">{spec.question_text || '(not set)'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1.5">Confidence floor</p>
                <div className="space-y-1">
                  {CONFIDENCE_FLOOR_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      data-testid="scaffolded-option"
                      onClick={() => setSpec(s => ({ ...s, confidence_floor: opt.value }))}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors',
                        spec.confidence_floor === opt.value
                          ? 'border-echo-indigo bg-echo-indigo/5 text-deep-ink font-medium'
                          : 'border-border-gray text-slate-600 hover:border-echo-indigo/40'
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setStep(2)}>Back</Button>
            </div>
          </div>
        )}
      </div>

      {/* Right rail — live spec */}
      <div className="lg:w-[55%] p-6 bg-soft-panel/30 space-y-5" data-testid="spec-rail">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-deep-ink">Bulk query spec</h2>
          {justSaved && (
            <span className="text-xs text-validation-green flex items-center gap-1">
              <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
              Just saved · {Object.values(spec).filter(Boolean).length} fields set
            </span>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-xs text-slate-400 mb-1">Research question</p>
            <p className="text-sm text-slate-700 min-h-[1.5rem]">{spec.question_text || <span className="text-slate-300">auto-populating...</span>}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Stimuli</p>
            <p className="text-sm text-slate-700">
              {spec.stimuli.length > 0 ? `${spec.stimuli.length} stimuli` : 'None (open-ended)'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Twins included</p>
            <p className="text-sm text-deep-ink">
              {twinsIncluded > 0 ? twinsIncluded.toLocaleString() : '—'} validated twins
              {spec.include_low_confidence && <span className="text-xs text-slate-400 ml-1">(incl. low confidence)</span>}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Confidence floor</p>
            <p className="text-sm text-slate-700">{spec.confidence_floor} Wilson lower</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Segments</p>
            <p className="text-sm text-slate-700">
              {spec.segments_filter.length > 0 ? spec.segments_filter.join(', ') : 'All segments'}
            </p>
          </div>
        </div>

        {/* Cost + time estimate */}
        <div className="flex items-center gap-4 py-3 border-y border-border-gray">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} />
            {estimatedSecs !== null ? `Estimated: ~${estimatedSecs}s` : 'Build twins to estimate'}
          </div>
          {estimatedCost !== null && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <DollarSign className="h-3.5 w-3.5" strokeWidth={1.75} />
              ~${estimatedCost}
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Zap className="h-3.5 w-3.5" strokeWidth={1.75} />
            {twinsIncluded > 0 ? `${twinsIncluded.toLocaleString()} twins` : 'No twins yet'}
          </div>
        </div>

        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={handleRun}
          disabled={!spec.question_text.trim() || running}
          data-testid="primary-cta"
        >
          {running ? 'Running...' : 'Run bulk query'}
        </Button>

        <p className="text-xs text-slate-400">
          Decision support only. Final review stays with your team before any client share or rollout.
        </p>
      </div>
    </div>
    </div>
  )
}
